import { supabase } from "./supabase";
import type { FaixaProgressiva, ParametrosFolha } from "./folhaDp";
import { lerParametrosFolha, salvarFaixas, salvarParametrosFolha } from "./hubDp";
import type { ParametrosDas } from "./dasMei";
import { lerParametrosDas } from "./simuladorMei";

/**
 * O IO da aba **Admin → Parâmetros**: os números que expiram em janeiro.
 *
 * Duas tabelas, uma tela. `dp_parametros`/`dp_faixas` alimentam o HUB DP (000108)
 * e `mei_parametros` alimenta o Simulador MEI (000098) — as duas nasceram com o
 * mesmo problema: só se corrigem por SQL, e o primeiro dia útil de janeiro chega
 * todo ano.
 *
 * Zero regra aqui. As RPCs já validam (recusam zero, exigem tabela progressiva
 * coerente, conferem o portão), e reescrever essas checagens na tela criaria a
 * segunda definição delas. O que esta camada faz é ler, escrever e **devolver a
 * mensagem do banco** — as recusas são informativas de propósito ("a alíquota da
 * faixa 2 é menor que a anterior") e quem está do outro lado é quem mantém a
 * tabela.
 *
 * ---------------------------------------------------------------------------
 * O salário mínimo está em DUAS tabelas, e isto é uma dívida que eu criei
 * ---------------------------------------------------------------------------
 * `dp_parametros.salario_minimo` e `mei_parametros.salario_minimo` guardam o
 * mesmo número — medido em 25/08: R$ 1.621,00 nas duas. Eu pus o campo na
 * `000108` sem notar que a `000098` já o tinha, e é a "segunda definição do mesmo
 * número" que a doutrina da casa combate.
 *
 * A consequência é operacional e concreta: em janeiro, quem mudar num lugar e
 * esquecer o outro deixa **uma ferramenta certa e a outra errada**, e nada avisa.
 * O HUB DP calcularia insalubridade sobre R$ 1.621 enquanto o Simulador MEI
 * cobraria INSS sobre o mínimo novo.
 *
 * O conserto DEFINITIVO é uma fonte só — uma função do hub que devolva o salário
 * mínimo, e as duas ferramentas lendo dela. Não fiz agora porque exigiria mexer
 * em `mei_das_parametros`, que está provada e no ar, e não era o que Victor
 * pediu. Está nomeado como pendência no runbook.
 *
 * O que ESTA camada faz enquanto isso: `salvarSalarioMinimo` grava nos **dois**
 * lugares numa chamada, e `lerParametros` devolve `salarioMinimoDivergente` para
 * a tela poder gritar se alguém já tiver mexido por SQL. Não conserta a dívida —
 * mas impede que ela vire um número errado na frente do aluno.
 */

export interface ParametrosDoAdmin {
  folha: ParametrosFolha | null;
  mei: ParametrosDas | null;
  /**
   * True quando as duas tabelas discordam do salário mínimo.
   *
   * Nunca deveria acontecer pela tela (ela grava nos dois), então é sinal de que
   * alguém mexeu por SQL num só — e a tela precisa dizer isso alto.
   */
  salarioMinimoDivergente: boolean;
}

/** Lê as duas tabelas de uma vez. `null` em qualquer campo = falhou aquele lado. */
export async function lerParametros(): Promise<ParametrosDoAdmin> {
  const [folha, mei] = await Promise.all([lerParametrosFolha(), lerParametrosDas()]);
  return {
    folha,
    mei,
    salarioMinimoDivergente:
      folha !== null && mei !== null && folha.salarioMinimo !== mei.salario_minimo,
  };
}

/**
 * O salário mínimo, nas DUAS tabelas.
 *
 * Duas chamadas e não uma transação: são RPCs separadas, e o PostgREST não as
 * envolve juntas. Se a segunda falhar, a primeira já gravou — e é por isso que a
 * função devolve as duas mensagens em vez de só a primeira: a tela precisa poder
 * dizer "gravou na folha, não gravou no MEI", que é um estado que alguém tem de
 * consertar à mão.
 *
 * Uma transação de verdade só existiria com o conserto definitivo (fonte única),
 * e é mais um motivo para ele.
 */
export async function salvarSalarioMinimo(valor: number): Promise<string | null> {
  const naFolha = await salvarParametrosFolha({ salarioMinimo: valor });
  const { error } = await supabase.rpc("mei_atualizar_parametros", {
    p_salario_minimo: valor,
  });
  const noMei = error ? error.message : null;

  if (naFolha && noMei) return `nas duas tabelas: ${naFolha} · ${noMei}`;
  if (naFolha) return `a folha recusou (${naFolha}) — o MEI foi gravado. Corrija e salve de novo.`;
  if (noMei) return `o MEI recusou (${noMei}) — a FOLHA JÁ FOI GRAVADA. Os dois estão divergentes agora.`;
  return null;
}

/** Os escalares da folha, menos o salário mínimo (que tem porta própria). */
export async function salvarEscalaresDaFolha(campos: {
  deducaoDependente?: number;
  descontoSimplificado?: number;
  reducaoLimiteIntegral?: number;
  reducaoValorIntegral?: number;
  reducaoLimiteFinal?: number;
  reducaoBase?: number;
  reducaoCoeficiente?: number;
  referenciaInss?: string;
  referenciaIrrf?: string;
}): Promise<string | null> {
  return salvarParametrosFolha(campos);
}

/** Uma tabela progressiva inteira. Ver `dp_definir_faixas` (000108). */
export async function salvarTabela(
  tributo: "inss" | "irrf",
  faixas: FaixaProgressiva[],
): Promise<string | null> {
  return salvarFaixas(tributo, faixas);
}

/** Os parâmetros do DAS do MEI, menos o salário mínimo. */
export async function salvarParametrosMei(campos: {
  limite?: number;
  limiteCaminhoneiro?: number;
  aliquotaInssGeral?: number;
  aliquotaInssCaminhoneiro?: number;
  icms?: number;
  iss?: number;
}): Promise<string | null> {
  const { error } = await supabase.rpc("mei_atualizar_parametros", {
    p_limite: campos.limite ?? null,
    p_limite_caminhoneiro: campos.limiteCaminhoneiro ?? null,
    p_salario_minimo: null,
    p_aliquota_inss_geral: campos.aliquotaInssGeral ?? null,
    p_aliquota_inss_caminhoneiro: campos.aliquotaInssCaminhoneiro ?? null,
    p_das_icms: campos.icms ?? null,
    p_das_iss: campos.iss ?? null,
  });
  return error ? error.message : null;
}
